import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import DateField from "../components/DateField";
import TimeField from "../components/TimeField";
import ReorderArrows from "../components/ReorderArrows";
import { useReorderableList } from "../utils/useReorderableList";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams } from "react-router-dom";
import { TransicionEstadoDialog } from "../components/TransicionEstadoDialog";

import {
  createCustomDate,
  formatearFechaDDMMYY,
  formatearFechaHoraYYYYMMDDHHMMSS,
  formatearHoraHHMM
} from "../utils/dates";
import { showSuccessMessage } from "../redux/appSlice";
import { useDispatch } from "react-redux";
import { useRequestHandler } from "../utils/useRequestHandler";
import { useTypeThenRun } from "../utils/useTypeThenRun";
import Servicios from "../services";

// Un día siguiente a una fecha dada, sin depender de moment para esto
// puntual (no hay ningún addDays genérico en utils/dates.js todavía).
const addDias = (fecha, dias) => {
  const d = new Date(fecha);
  d.setDate(d.getDate() + dias);
  return d;
};

// Fecha "sugerida por defecto" al suspender una etapa (regla real del
// carnaval, no bloqueante — el admin la puede pisar a mano en el DateField
// antes de confirmar): el día siguiente a la última etapa de la MISMA rueda
// por fecha (no por número — tras varias suspensiones la "última" puede ser
// cualquier número), excluyendo a la propia etapa que se está suspendiendo.
const calcularFechaSugeridaSuspension = (etapaActual, listaEtapas) => {
  const otras = (listaEtapas || []).filter(
    e =>
      e.ruedaConcursoId === etapaActual?.ruedaConcursoId &&
      e.id !== etapaActual?.id &&
      e.fecha &&
      e.fecha !== "0001-01-01T00:00:00"
  );
  if (otras.length === 0) {
    return etapaActual?.fecha && etapaActual.fecha !== "0001-01-01T00:00:00"
      ? new Date(etapaActual.fecha)
      : new Date();
  }
  const maxFecha = otras.reduce(
    (max, e) => (new Date(e.fecha) > max ? new Date(e.fecha) : max),
    new Date(otras[0].fecha)
  );
  return addDias(maxFecha, 1);
};

export const EtapasConcurso = () => {
  const { _concursoId } = useParams();
  const [fecha, setFecha] = useState(new Date());
  const [textoBusqueda, setTextoBusqueda] = useState("");
  const [textoBusquedaTmp, setTextoBusquedaTmp] = useState("");
  const [conjuntos, setConjuntos] = useState([]);
  const [conjuntosActuan, setConjuntosActuan] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [etapa, setEtapa] = useState();
  const [etapaId, setEtapaId] = useState(0);
  const dispatch = useDispatch();
  const sendRequest = useRequestHandler();
  const [obtener, setObtener] = useState(false);

  // Suspender una etapa puntual.
  const [fechaSuspension, setFechaSuspension] = useState(new Date());
  const [dialogSuspenderAbierto, setDialogSuspenderAbierto] = useState(false);
  const [suspensionConfirmada, setSuspensionConfirmada] = useState(false);

  // Correr el inicio de una rueda completa (independiente de la etapa
  // seleccionada arriba — no hace falta tener ninguna fila clickeada).
  const [ruedaSeleccionada, setRuedaSeleccionada] = useState("");
  const [fechaInicioRueda, setFechaInicioRueda] = useState(new Date());
  const [dialogCorrerInicioAbierto, setDialogCorrerInicioAbierto] = useState(false);
  const [corrimientoConfirmado, setCorrimientoConfirmado] = useState(false);

  useEffect(() => {
    sendRequest(Servicios.ObtenerEtapas, [_concursoId], data => {
      setEtapas(data?.data);
    });
  }, [obtener]);

  useEffect(() => {
    if (etapaId) {
      sendRequest(Servicios.ObtenerEtapaPorId, [etapaId], data => {
        setEtapa(data?.data);
        setFecha(new Date(data?.data?.fecha && data?.data?.fecha != "0001-01-01T00:00:00" ? data?.data?.fecha : ""));
        setConjuntosActuan(
          data?.data?.conjuntos.map(x => {
            return { ...x, fechaHora: formatearHoraHHMM(x.fechaHora) };
          })
        );
        setFechaSuspension(calcularFechaSugeridaSuspension(data?.data, etapas));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaId]);

  const handleConfirmarSuspension = async () => {
    await Servicios.SuspenderEtapa(
      etapaId,
      formatearFechaHoraYYYYMMDDHHMMSS(fechaSuspension)
    );
    setSuspensionConfirmada(true);
  };

  const handleCerrarDialogoSuspension = () => {
    setDialogSuspenderAbierto(false);
    if (suspensionConfirmada) {
      setSuspensionConfirmada(false);
      setEtapaId(0);
      setObtener(!obtener);
    }
  };

  const ruedasDisponibles = [
    ...new Map(etapas.map(e => [e.ruedaConcursoId, e.ruedaConcursoNombre])).entries()
  ];

  const etapasDeRuedaSeleccionada = ruedaSeleccionada
    ? etapas
        .filter(e => e.ruedaConcursoId === ruedaSeleccionada)
        .sort((a, b) => a.numeroEtapa - b.numeroEtapa)
    : [];

  const numeroEtapaMinimo = etapasDeRuedaSeleccionada.length
    ? Math.min(...etapasDeRuedaSeleccionada.map(e => e.numeroEtapa))
    : 0;

  const accionesCorrerInicio = [
    ...etapasDeRuedaSeleccionada.map(e => {
      const fechaNueva = addDias(fechaInicioRueda, e.numeroEtapa - numeroEtapaMinimo);
      const fechaActualTexto =
        e.fecha && e.fecha !== "0001-01-01T00:00:00"
          ? formatearFechaDDMMYY(e.fecha)
          : "sin fecha";
      return `${e.nombre} pasa del ${fechaActualTexto} al ${formatearFechaDDMMYY(fechaNueva)}.`;
    }),
    "Se notifica a todos los usuarios de la app."
  ];

  const handleConfirmarCorrerInicio = async () => {
    await Servicios.CorrerInicioRueda(
      _concursoId,
      ruedaSeleccionada,
      formatearFechaHoraYYYYMMDDHHMMSS(fechaInicioRueda)
    );
    setCorrimientoConfirmado(true);
  };

  const handleCerrarDialogoCorrerInicio = () => {
    setDialogCorrerInicioAbierto(false);
    if (corrimientoConfirmado) {
      setCorrimientoConfirmado(false);
      setObtener(!obtener);
    }
  };

  const columnsConjuntos = [
    { field: "nombre", headerName: "Nombre", flex: 99 },

    {
      field: "actionAdd",
      headerName: "",
      sortable: false,
      align: "right",
      flex: 1,
      renderCell: params => {
        const onClick = e => {
          setConjuntos(
            conjuntos?.map((x, index) => {
              if (x.id == params?.row?.id) {
                setConjuntosActuan([...conjuntosActuan, x]);
                return { ...x, agregado: true };
              } else {
                return x;
              }
            })
          );
          setTextoBusqueda("");
        };

        return <AddCircleOutline className="action-icon" onClick={onClick} />;
      }
    }
  ];

  const {
    moveUp: moveConjuntoActuaUp,
    moveDown: moveConjuntoActuaDown,
    canMoveUp: canMoveConjuntoActuaUp,
    canMoveDown: canMoveConjuntoActuaDown
  } = useReorderableList(conjuntosActuan, setConjuntosActuan);

  const columnsConjuntosActuan = [
    {
      field: "fechaHora",
      headerName: "Hora",
      flex: 20,
      sortable: false,
      renderCell: params => {
        const indice = conjuntosActuan.indexOf(params.row);
        return (
          <TimeField
            size="small"
            value={params.row.fechaHora}
            ariaLabel="Hora de actuación"
            onChange={newValue => {
              let arr = [...conjuntosActuan];
              arr[indice] = { ...arr[indice], fechaHora: newValue };
              setConjuntosActuan(arr);
            }}
          />
        );
      }
    },
    { field: "nombre", headerName: "Nombre", flex: 99 },
    {
      field: "actionReorder",
      headerName: "",
      sortable: false,
      align: "right",
      flex: 2,
      renderCell: params => {
        const indice = conjuntosActuan.indexOf(params.row);
        return (
          <ReorderArrows
            onMoveUp={() => moveConjuntoActuaUp(indice)}
            onMoveDown={() => moveConjuntoActuaDown(indice)}
            canMoveUp={canMoveConjuntoActuaUp(indice)}
            canMoveDown={canMoveConjuntoActuaDown(indice)}
          />
        );
      }
    },
    {
      field: "actionDelete",
      headerName: "",
      sortable: false,
      align: "right",
      flex: 1,
      renderCell: params => {
        const onClick = e => {
          setConjuntosActuan(
            conjuntosActuan.filter(x => x.id != params?.row?.id)
          );
        };

        return <DeleteIcon className="action-icon" onClick={onClick} />;
      }
    }
  ];

  useTypeThenRun(textoBusquedaTmp, () => setTextoBusqueda(textoBusquedaTmp));

  useEffect(() => {
    sendRequest(
      Servicios.ConjuntosConcursan,
      [textoBusqueda, _concursoId],
      data => {
        setConjuntos(data?.data);
      }
    );
  }, [textoBusqueda, conjuntosActuan.length]);

  const handleGuardar = () => {
    sendRequest(
      Servicios.CargarEtapaConcurso,
      [
        {
          id: etapaId,
          fecha: formatearFechaHoraYYYYMMDDHHMMSS(fecha),
          conjuntos: conjuntosActuan.map(x => {
            return {
              conjuntoId: x.id,
              fechaHora:
                x.fechaHora?.length == 5
                  ? formatearFechaHoraYYYYMMDDHHMMSS(
                    createCustomDate(new Date(fecha), x.fechaHora)
                  )
                  : formatearFechaHoraYYYYMMDDHHMMSS(x.fechaHora)
            };
          })
        }
      ],
      () => {
        dispatch(showSuccessMessage("Etapa cargada correctamente"));
        setObtener(!obtener);
      }
    );
  };

  return (
    <div className="card-modern">
      <div className="page-header">
        <h3 className="page-title">Etapas del concurso</h3>
      </div>
      <div
        className="grid-wrapper my-3"
        style={{ height: 400, overflow: "auto" }}
      >
        <DataGrid
          onRowClick={e => {
            setEtapaId(e.row.id);
          }}
          rows={etapas}
          columns={[
            {
              field: "fecha",
              headerName: "Fecha",
              renderCell: params => {
                return (
                  <>
                    {params.row.fecha
                      ? formatearFechaDDMMYY(params.row.fecha)
                      : ""}
                  </>
                );
              }
            },
            { field: "nombre", headerName: "Etapa", flex: 30 }
          ]}
          pagination={false}
          checkboxSelection={false}
        />
      </div>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />

      <div className="mb-3 m-2">
        <h5>Corrimiento de inicio de rueda</h5>
        <p className="text-sm">
          Para cuando el arrastre de suspensiones obliga a mover el arranque
          previsto de toda una rueda (ej. Segunda Rueda) — no solo una etapa
          puntual. Recalcula consecutivas, desde la fecha elegida, TODAS las
          etapas de la rueda seleccionada, pisando la fecha que tuvieran
          antes.
        </p>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <FormControl size="small" className="w-full md:w-1/4">
            <InputLabel id="rueda-label">Rueda</InputLabel>
            <Select
              labelId="rueda-label"
              label="Rueda"
              value={ruedaSeleccionada}
              inputProps={{ "aria-label": "Rueda" }}
              onChange={e => setRuedaSeleccionada(e.target.value)}
            >
              {ruedasDisponibles.map(([id, nombre]) => (
                <MenuItem key={id} value={id}>
                  {nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="w-full md:w-1/4">
            <DateField
              className="w-full"
              value={fechaInicioRueda}
              onChange={setFechaInicioRueda}
              ariaLabel="Nueva fecha de inicio de la rueda"
              label="Nueva fecha de inicio"
            />
          </div>
          <Button
            variant="outlined"
            disabled={!ruedaSeleccionada}
            onClick={() => setDialogCorrerInicioAbierto(true)}
          >
            Correr inicio de rueda
          </Button>
        </div>
      </div>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />
      {etapaId != 0 && (
        <>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-1/4">
              <div className="mb-3">
                <DateField
                  className="w-full"
                  value={fecha}
                  onChange={setFecha}
                  ariaLabel="Fecha de la etapa"
                />
              </div>
              <div className="mb-3">
                <TextField
                  className="w-full my-1"
                  size="small"
                  label="Buscar conjunto"
                  value={textoBusquedaTmp}
                  onChange={e => setTextoBusquedaTmp(e.target.value)}
                />
              </div>

              <div className="grid-wrapper" style={{ height: 400, overflow: "auto" }}>
                <DataGrid
                  pagination={false}
                  disableColumnFilter
                  disableColumnMenu
                  hideFooterPagination
                  rows={conjuntos}
                  columns={columnsConjuntos}
                  checkboxSelection={false}
                  className="no-header"
                />
              </div>
            </div>
            <div className="w-full md:w-3/4">
              <div className="grid-wrapper" style={{ height: 400, overflow: "auto" }}>
                <DataGrid
                  pagination={false}
                  disableColumnFilter
                  disableColumnMenu
                  disableDensitySelector
                  hideFooterPagination
                  rows={conjuntosActuan}
                  columns={columnsConjuntosActuan}
                  checkboxSelection={false}
                  className="conjuntos-actuan-grid"
                />
              </div>
            </div>
          </div>
          <hr className="border-gray-200 dark:border-gray-700 my-4" />
          <div className="flex justify-center gap-4 w-auto">
            <div className="text-end">
              <Button
                variant={"outlined"}
                onClick={() => {
                  window.history.go(-1);
                }}
              >
                Cancelar
              </Button>
            </div>
            <div className="text-end">
              <Button variant={"contained"} onClick={handleGuardar}>
                Guardar
              </Button>
            </div>
          </div>
          <hr className="border-gray-200 dark:border-gray-700 my-4" />
          <div className="mb-3 m-2">
            <h5>Suspender esta etapa</h5>
            <p className="text-sm">
              No cambia el número de etapa ni las fechas de las demás — solo
              mueve esta etapa (y la programación pública del Teatro de
              Verano de esa noche) a la fecha nueva. La fecha ya viene
              sugerida (día siguiente a la última etapa de esta misma rueda);
              se puede cambiar antes de confirmar.
            </p>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="w-full md:w-1/4">
                <DateField
                  className="w-full"
                  value={fechaSuspension}
                  onChange={setFechaSuspension}
                  ariaLabel="Nueva fecha de la etapa suspendida"
                  label="Nueva fecha"
                />
              </div>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDialogSuspenderAbierto(true)}
              >
                Suspender etapa
              </Button>
            </div>
          </div>
        </>
      )}

      <TransicionEstadoDialog
        open={dialogSuspenderAbierto}
        onClose={handleCerrarDialogoSuspension}
        titulo="Suspender etapa"
        acciones={[
          `${etapa?.nombre ?? "La etapa"} se suspende y pasa al ${formatearFechaDDMMYY(fechaSuspension)}.`,
          "La programación pública del Teatro de Verano de esa noche se traslada a la fecha nueva.",
          "Se notifica a todos los usuarios de la app."
        ]}
        puedeConfirmar={true}
        mensajeExito="Etapa suspendida correctamente."
        onConfirmar={handleConfirmarSuspension}
      />

      <TransicionEstadoDialog
        open={dialogCorrerInicioAbierto}
        onClose={handleCerrarDialogoCorrerInicio}
        titulo="Correr inicio de rueda"
        acciones={accionesCorrerInicio}
        puedeConfirmar={etapasDeRuedaSeleccionada.length > 0}
        mensajeExito="Inicio de rueda corrido correctamente."
        onConfirmar={handleConfirmarCorrerInicio}
      />
    </div>
  );
};

export default withAuthenticationRequired(EtapasConcurso, {
  onRedirecting: () => <Loading />
});
